import { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  TablePagination,
  IconButton,
  Autocomplete
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  createAmenaza,
  deleteAmenaza,
  getAmenazasPorEmpresa,
  updateAmenaza,
} from "../../services/amenazas/amenazasData";
import { getTipoAmenazas } from "../../services/amenazas/tipoamenaza/tipoAmenazasData";
import { useAuth } from "../../context/AuthContext";
import { getProbabilidad } from "../../services/probabilidad/probabilidadData";
import { toast } from "react-toastify";

const TablaAmenazas = () => {
  const [datos, setDatos] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [tiposIds, setTiposIds] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const { user } = useAuth();
  const [modificarIndex, setModificarIndex] = useState(-1);
  const emp_codigo = user?.emp_codigo;
  const [probabilidades, setProbabilidades] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [formulario, setFormulario] = useState({
    Tipo: "",
    FechaInicio: "",
    Probabilidad: "",
    ProbabilidadId: "",
    Identificacion: "",
    Nombre: "",
    Descripción: "",
    Observacion: "",
    FechaInactividad: null,
  });

  useEffect(() => {
    const fetchProbabilidades = async () => {
      if (emp_codigo) {
        const probData = await getProbabilidad(emp_codigo);
        setProbabilidades(probData);
      }
    };
    fetchProbabilidades();
  }, [emp_codigo]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (emp_codigo) {
          const amenazasData = await getAmenazasPorEmpresa(emp_codigo);
          setDatos(amenazasData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [emp_codigo]);

  useEffect(() => {
    const fetchTipos = async () => {
      try {
        const tiposData = await getTipoAmenazas();
        setTipos(Array.isArray(tiposData) ? tiposData : []);
      } catch (error) {
        console.error('Error fetching tipos:', error);
        setTipos([]);
      }
    };
    fetchTipos();
  }, []);

  useEffect(() => {
    if (Array.isArray(tipos) && tipos.length > 0) {
      const tiposIds1 = tipos.map((item) => item.tia_codigo);
      setTiposIds(tiposIds1);
    } else {
      setTiposIds([]);
    }
  }, [tipos]);

  const handleBusquedaChange = (event) => {
    setBusqueda(event.target.value);
    setPage(0);
  };

  const handleDateChange = (newValue) => {
    const formattedDate = dayjs(newValue).format("YYYY-MM-DD");
    setFormulario({
      ...formulario,
      FechaInicio: formattedDate,
    });
  };
  const handleFechaInactividadChange = (newValue) => {
    const formattedDate = dayjs(newValue).format("YYYY-MM-DD");
    setFormulario({
      ...formulario,
      FechaInactividad: formattedDate,
    });
  };

  const handleInsert = () => {
    setModificarIndex(-1);
    setFormulario({
      Tipo: "",
      TipoId: "",
      FechaInicio: "",
      Probabilidad: "",
      ProbabilidadId: "",
      Identificacion: generarIdentificacion(),
      Nombre: "",
      Descripción: "",
      Observacion: "",
      FechaInactividad: null,
    });
    setModalOpen(true);
  };

  const handleFormChange = (event) => {
    setFormulario({
      ...formulario,
      [event.target.name]: event.target.value,
    });
  };

  const buildAmenazaPayload = (includeEmpresa = false) => {
    const payload = {
      tia_codigo: formulario.TipoId ? Number(formulario.TipoId) : null,
      pro_codigo: formulario.ProbabilidadId ? Number(formulario.ProbabilidadId) : null,
      ame_fecha_inicio: formulario.FechaInicio || null,
      ame_identificacion: formulario.Identificacion?.trim(),
      ame_nombre: formulario.Nombre?.trim(),
      ame_descripcion: formulario.Descripción?.trim(),
      ame_fecha_inactividad: formulario.FechaInactividad || null,
      ame_observacion: formulario.Observacion?.trim(),
    };

    if (includeEmpresa) {
      payload.emp_codigo = emp_codigo;
    }

    return payload;
  };

  const handleGuardar = async () => {
    const camposFaltantes = [];
    if (!formulario.TipoId) camposFaltantes.push("Tipo de Amenaza");
    if (!formulario.ProbabilidadId) camposFaltantes.push("Probabilidad");
    if (!formulario.FechaInicio) camposFaltantes.push("Fecha de Inicio");
    if (!formulario.Nombre) camposFaltantes.push("Nombre");
    if (!formulario.Descripción) camposFaltantes.push("Descripción");
    if (!formulario.Observacion) camposFaltantes.push("Observación");

    if (camposFaltantes.length > 0) {
      toast.warning(`Faltan campos obligatorios: ${camposFaltantes.join(", ")}`);
      return;
    }
    try {
      const hoy = dayjs().format("YYYY-MM-DD");
      const dataToSubmit = buildAmenazaPayload(true);
      if (!dataToSubmit.ame_fecha_inactividad) {
        dataToSubmit.ame_fecha_inactividad = hoy;
      }
      if (modificarIndex !== -1) {
        await updateAmenaza(modificarIndex, dataToSubmit);
        toast.success("Amenaza modificada correctamente");
      } else {
        await createAmenaza(dataToSubmit);
        toast.success("Amenaza creada correctamente");
      }
      const updatedData = await getAmenazasPorEmpresa(emp_codigo);
      setDatos(updatedData);
      setModalOpen(false);
      setFormulario({
        Tipo: '',
        TipoId: '',
        FechaInicio: '',
        Identificacion: '',
        Nombre: '',
        Descripción: '',
        Observacion: '',
        FechaInactividad: null,
      });
    } catch (error) {
      toast.error("Error al guardar la amenaza: " + error.message);
    }
  };

  const handleCancelar = () => {
    setModalOpen(false);
    setModificarIndex(-1);
    setFormulario({
      Tipo: "",
      FechaInicio: "",
      Identificacion: "",
      Nombre: "",
      Descripción: "",
      Observacion: "",
      FechaInactividad: null,
    });
  };

  const handleModificar = (index) => {
    const amenaza = datos[index];
    const selectedTipo = tipos.find(
      (tipo) => tipo.tia_codigo === amenaza.tia_codigo
    );

    setModificarIndex(amenaza.ame_codigo);
    setFormulario({
      TipoId: selectedTipo ? selectedTipo.tia_codigo : "",
      Tipo: selectedTipo ? selectedTipo.tia_descripcion : "",
      FechaInicio: amenaza.ame_fecha_inicio
        ? amenaza.ame_fecha_inicio.split("T")[0]
        : "",
      ProbabilidadId: amenaza.pro_codigo || "",
      Probabilidad: amenaza.ame_probabilidad || "",
      Identificacion: amenaza.ame_identificacion,
      Nombre: amenaza.ame_nombre,
      Descripción: amenaza.ame_descripcion,
      Observacion: amenaza.ame_observacion,
      FechaInactividad: amenaza.ame_fecha_inactividad
        ? amenaza.ame_fecha_inactividad.split("T")[0]
        : "",
    });
    setModalOpen(true);
  };

  const handleEliminar = (index) => {
    setDeleteIndex(index);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    const codigoToDelete = datos[deleteIndex].ame_codigo;
    try {
      await deleteAmenaza(codigoToDelete);
      const updatedData = await getAmenazasPorEmpresa(emp_codigo);
      setDatos(updatedData);
      toast.success("Amenaza eliminada correctamente");
      setConfirmOpen(false);
      setDeleteIndex(null);
    } catch (error) {
      setConfirmOpen(false);
      const errorMsg =
        error?.message ||
        error?.response?.data?.message ||
        "No se puede eliminar la amenaza porque está siendo utilizada en el sistema.";
      const status = error?.status || error?.response?.status;

      if (
        status === 400 ||
        errorMsg.toLowerCase().includes("no se puede eliminar") ||
        errorMsg.toLowerCase().includes("asociada") ||
        errorMsg.toLowerCase().includes("utilizada") ||
        error?.response?.data?.errno === 1451 ||
        (error?.response?.data?.code && error.response.data.code.includes("ER_ROW_IS_REFERENCED"))
      ) {
        setErrorMessage(errorMsg);
        setErrorModalOpen(true);
      } else {
        toast.error(errorMsg);
      }
      setDeleteIndex(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setDeleteIndex(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const datosFiltrados = datos.filter((dato) =>
    dato.ame_nombre && dato.ame_nombre.toLowerCase().includes(busqueda.toLowerCase())
  );

  function generarIdentificacion() {
    const letras = Array(3)
      .fill(0)
      .map(() => String.fromCharCode(65 + Math.floor(Math.random() * 26)))
      .join('');
    const numeros = String(Math.floor(Math.random() * 1000)).padStart(3, '0');
    return `${letras}-${numeros}`;
  }

  // Obtener descripción del tipo de amenaza a partir de su código
  const getTipoDescripcion = (codigo) => {
    const tipo = tipos.find((t) => t.tia_codigo === codigo);
    return tipo ? tipo.tia_descripcion : codigo;
  };

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder="Buscar amenaza..."
          value={busqueda}
          onChange={handleBusquedaChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: 300 } }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleInsert}
          sx={{
            backgroundColor: '#001F3F',
            color: '#fff',
            '&:hover': { backgroundColor: '#003366' },
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Nueva Amenaza
        </Button>
      </Box>

      <TableContainer>
        <Table sx={{ minWidth: 750 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Tipo</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Fecha Inicio</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Identificación</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Descripción</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Observación</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Fecha Inactividad</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {datosFiltrados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  <Typography variant="body1" sx={{ color: '#666' }}>
                    No hay registros para esta empresa.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              datosFiltrados
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((dato, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{getTipoDescripcion(dato.tia_codigo)}</TableCell>
                    <TableCell>{dato.ame_fecha_inicio ? dato.ame_fecha_inicio.split("T")[0] : ''}</TableCell>
                    <TableCell>{dato.ame_identificacion}</TableCell>
                    <TableCell>{dato.ame_nombre}</TableCell>
                    <TableCell>{dato.ame_descripcion}</TableCell>
                    <TableCell>{dato.ame_observacion}</TableCell>
                    <TableCell>
                      {dato.ame_fecha_inactividad
                        ? dato.ame_fecha_inactividad.split("T")[0]
                        : dato.ame_fecha_inactividad}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <IconButton
                          onClick={() => handleModificar(index + (page * rowsPerPage))}
                          size="small"
                          sx={{ color: "#666" }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleEliminar(index + (page * rowsPerPage))}
                          size="small"
                          sx={{ color: "#d32f2f" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={datosFiltrados.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
      />

      {/* Modal Formulario */}
      <Dialog
        open={modalOpen}
        onClose={handleCancelar}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 2, fontWeight: 600, color: '#001F3F', fontSize: '1.25rem' }}>
          {modificarIndex !== -1 ? "Modificar Amenaza" : "Nueva Amenaza"}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Autocomplete
                disablePortal
                id="combo-box-demo"
                name="Tipo"
                value={
                  tipos
                    .map((tipo) => ({ label: tipo.tia_descripcion, value: tipo.tia_codigo }))
                    .find(opt => opt.value === formulario.TipoId) || null
                }
                options={tipos.map((tipo) => ({
                  label: tipo.tia_descripcion,
                  value: tipo.tia_codigo
                }))}
                sx={{ width: "100%" }}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onChange={(event, newValue) => {
                  setFormulario({
                    ...formulario,
                    TipoId: newValue ? newValue.value : "",
                    Tipo: newValue ? newValue.label : "",
                  });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Tipos de Amenazas" variant="outlined" />
                )}
              />
              <Autocomplete
                disablePortal
                id="combo-box-probabilidad"
                name="Probabilidad"
                value={
                  probabilidades
                    .map((prob) => ({ label: prob.pro_probabilidad, value: prob.pro_codigo }))
                    .find(opt => opt.value === formulario.ProbabilidadId) || null
                }
                options={probabilidades.map((prob) => ({
                  label: prob.pro_probabilidad,
                  value: prob.pro_codigo
                }))}
                sx={{ width: "100%" }}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onChange={(event, newValue) => {
                  setFormulario({
                    ...formulario,
                    ProbabilidadId: newValue ? newValue.value : "",
                    Probabilidad: newValue ? newValue.label : "",
                  });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Probabilidad" variant="outlined" />
                )}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Fecha de Inicio"
                  name="FechaInicio"
                  value={dayjs(formulario.FechaInicio)}
                  onChange={handleDateChange}
                  minDate={dayjs()}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  sx={{ width: '100%' }}
                />
              </LocalizationProvider>
              <TextField
                label="Identificacion"
                name="Identificacion"
                fullWidth
                value={formulario.Identificacion}
                onChange={handleFormChange}
                variant="outlined"
              />
            </Box>

            <TextField
              label="Nombre"
              name="Nombre"
              fullWidth
              value={formulario.Nombre}
              onChange={handleFormChange}
              variant="outlined"
            />
            <TextField
              label="Descripción"
              name="Descripción"
              fullWidth
              value={formulario.Descripción}
              onChange={handleFormChange}
              variant="outlined"
              multiline
              rows={2}
            />
            <TextField
              label="Observación"
              name="Observacion"
              fullWidth
              value={formulario.Observacion}
              onChange={handleFormChange}
              variant="outlined"
              multiline
              rows={2}
            />
            {modificarIndex !== -1 && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Fecha de Inactividad"
                  name="FechaInactividad"
                  value={dayjs(formulario.FechaInactividad)}
                  onChange={handleFechaInactividadChange}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelar} sx={{ color: '#666' }}>Cancelar</Button>
          <Button
            onClick={handleGuardar}
            variant="contained"
            sx={{
              backgroundColor: '#001F3F',
              color: '#fff',
              '&:hover': { backgroundColor: '#003366' }
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Confirmación Eliminación */}
      <Dialog
        open={confirmOpen}
        onClose={handleCancelDelete}
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#001F3F', fontSize: '1.25rem' }}>
          Confirmar Eliminación
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#666' }}>
            ¿Está seguro que desea eliminar esta amenaza?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelDelete} sx={{ color: '#666' }}>Cancelar</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              backgroundColor: '#d32f2f',
              color: '#fff',
              '&:hover': { backgroundColor: '#c62828' }
            }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de error al eliminar */}
      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#001F3F', fontSize: '1.25rem' }}>
          No se puede eliminar
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
              {errorMessage || 'Esta amenaza no puede eliminarse porque está siendo utilizada en otro proceso.'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              Para eliminarla, primero debes eliminar o modificar todas las relaciones asociadas (valoraciones, vulnerabilidades, etc.).
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setErrorModalOpen(false)}
            variant="contained"
            sx={{ backgroundColor: '#001F3F', color: '#fff', '&:hover': { backgroundColor: '#003366' } }}
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TablaAmenazas;
